<template>
    <section
        v-if="question && feedback"
        class="mx-auto flex h-full min-h-[500px] w-full max-w-4xl flex-col items-center justify-center px-6 py-4 text-white lg:pt-6 xl:max-w-7xl"
    >
        <div
            class="flex min-h-0 w-full flex-1 items-center justify-center"
            style="max-height: 450px"
        >
            <img
                v-if="feedback.image?.url"
                :src="feedback.image.url"
                :alt="feedback.image.alt || ''"
                loading="eager"
                class="mx-auto h-full max-h-full w-auto object-contain"
            />
        </div>

        <div class="w-full flex-shrink-0 pt-3" style="max-height: 55%">
            <h2
                class="mb-4 text-center font-aktivCondensed text-3xl font-extrabold md:px-0 md:text-4xl"
            >
                {{ headline }}
            </h2>
            <div
                class="feedback mx-auto space-y-4 text-pretty text-center"
                v-html="feedback.bodyHtml"
            ></div>
            <div class="mx-auto mt-6 flex w-full justify-center">
                <button
                    type="button"
                    class="mx-auto inline-block w-3/4 rounded-full bg-white px-10 py-2.5 text-center font-semibold text-blue shadow-sm duration-150 hover:bg-transparent hover:text-white hover:ring-2 hover:ring-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky active:bg-transparent md:w-auto"
                    @click="next"
                >
                    {{ feedback.ctaLabel || "Next question" }}
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useQuizStore } from "../stores/quizStore";
import type { QuestionSlide } from "../types";

const props = defineProps<{ questionId: string }>();

const quiz = useQuizStore();
const router = useRouter();

const question = computed<QuestionSlide | undefined>(() =>
    quiz.getQuestionById(props.questionId),
);

const feedback = computed(() => question.value?.feedback);

const answerEntry = computed(() => quiz.getAnswer(props.questionId));
const isCorrect = computed(() => answerEntry.value?.isCorrect ?? null);

const headline = computed(() => {
    const fb = feedback.value;
    if (isCorrect.value === null) return "";
    return isCorrect.value
        ? (fb?.headlineCorrect ?? "Correct!")
        : (fb?.headlineIncorrect ?? "Not Quite.");
});

function next() {
    const idx = quiz.getQuestionIndex(props.questionId);
    const next = quiz.questions[idx + 1];

    if (!next) {
        router.push("/score");
        return;
    }

    const alreadyAnswered = quiz.isAnswered(next.questionId);

    if (alreadyAnswered) {
        router.replace({ name: "question", params: { id: next.questionId } });
        return;
    }

    router.push({ name: "questionIntro", params: { id: next.questionId } });
}

onMounted(() => {
    if (!feedback.value) return;
    if (!answerEntry.value) {
        router.replace(`/${props.questionId}`);
    }

    quiz.clearJustAnswered();

    const span = document.querySelector("[data-span]");
    if (!span) return;

    const correctText = feedback.value.spanCorrect?.trim();
    const incorrectText = feedback.value.spanIncorrect?.trim();

    if (isCorrect.value && correctText) {
        span.textContent = correctText;
    }

    if (!isCorrect.value && incorrectText) {
        span.textContent = incorrectText;
    }
});
</script>

<style>
.feedback h3 {
    font-size: 1.25rem /* 20px */;
    line-height: 1.75rem /* 28px */;
    font-weight: 700;
}
.feedback p {
    font-size: 1rem;
    line-height: 1.25rem;
    max-width: 62ch;
    margin-left: auto;
    margin-right: auto;
}
.feedback {
    max-width: 96ch;
}

@media (min-width: 992px) {
    .feedback h3 {
        font-size: 1.25rem;
        line-height: 1.75rem;
    }
    .feedback p {
        font-size: 1rem;
        line-height: 1.25rem;
    }
    .feedback span {
        font-size: 1rem;
        line-height: 1.25rem;
    }
}

@media (min-width: 1200px) {
    .feedback h3 {
        font-size: 1.5rem /* 24px */;
        line-height: 2rem /* 32px */;
    }
    .feedback p {
        font-size: 1.25rem;
        line-height: 1.75rem;
    }
    .feedback span {
        font-size: 1.25rem;
        line-height: 1.75rem;
    }
}
.active\:bg-transparent:active {
    background-color: transparent;
}
</style>
